import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { sortBy } from 'lodash';
import { showToast } from 'stores/ToastStore';
import formatMoney from 'utils/formatters/Money';
import { getShipmentDetailUrl } from 'utils/formatters/shipments';
import { getDocuments, getSignedUrl } from 'utils/DocumentsAPIUtils';
import { getShipment } from 'utils/ShipmentWebAPIUtils';
import CapabilityCircle from 'components/modules/user/CapabilityCircle';
import { Translation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

function sortJobs(lineItemGroups) {
  return sortBy(lineItemGroups, ['driverName', 'jobDeliverBy']);
}

function renderNoJobs() {
  return (
    <Translation>
      {(t, { i18n }) => (
        <div className="job-list">
          <h3 className="my-0">{t('NO_JOBS')}</h3>
        </div>
      )}
    </Translation>
  );
}

function renderJobLoad(load) {
  const rows = [];
  let pallets;
  for (let i = 0; i < load.length; i += 1) {
    pallets = load[i];
    rows.push(
      pallets.quantity +
        ' (' +
        pallets.width +
        'x' +
        pallets.height +
        'x' +
        pallets.length +
        ')',
    );
    if (i) {
      rows.push(<br />);
    }
  }
  return rows;
}

class JobHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      jobList: [],
    };
  }

  async componentDidMount() {
    try {
      const jobList = await Promise.all(
        sortJobs(this.props.lineItemGroups).map(await this.renderJobElement),
      );
      this.setState({ jobList });
    } catch (e) {
      console.error('job-history, failed to get signed URL: ', e);
    }
  }

  async handlePODClick (shipment_id, t, event) {
    const pod = await this.getPOD(shipment_id, t);
    if (pod) {
      const open = this.preparePODOpen(pod);
      open(event);
    }
  };

  preparePODOpen = (pod) => (event) => {
    event.preventDefault();
    const windowRef = window.open();
    this.openPOD(pod, windowRef);
  };

  openPOD = async (pod, windowRef) => {
    try {
      const { data: signedUrl } = await getSignedUrl(pod);

      windowRef.location = signedUrl?.getSignedUrl?.signedUrl;
    } catch (e) {
      console.error('job-history: error getting signed URL:', e);
    }
  };

  getPOD = async (shipment_id, t) => {
    try {
      const {
        entity: { item: shipment },
      } = await getShipment(shipment_id);
      if (!shipment) {
        throw new Error('Shipment Not Found');
      }
      const pickupAttempts = shipment.stops.filter(
        (s) => s.attempt && s.action === 'pickup',
      );
      const delivery = sortBy(shipment.stops, 'sequence').find(
        (s) => s.action === 'deliver' && !s.empty,
      );

      if (pickupAttempts.length || (delivery && delivery.attempt)) {
        return 'Attempt';
      }

      if (delivery) {
        const { data } = await getDocuments(
          [delivery.id],
          'proof_of_delivery',
          {
            order: {
              field: 'createdAt',
              direction: 'desc',
            },
            limit: 1,
          },
        );

        const pod = data?.getDocuments?.documents[0];

        if (pod) {
          return pod.key;
        }
      }
      showToast(t('SHIPMENT_MISSING_POD'), null , 'error');
    } catch (e) {
      console.error(e);
      showToast(t('ERROR_FETCHING_DOCUMENTS'), null ,'error');
    };
  };

  getPODLink = (shipment_id) => {
    const { t } = this.props;
    return (
      <td>
        <a onClick={(event) => this.handlePODClick(shipment_id, t , event)}>
          {t('POD')}
        </a>
      </td>
    );
  };

  renderJobElement = async (lineItemGroup) => {
    const { t } = this.props;
    if (!(lineItemGroup && lineItemGroup.shipment_id)) {
      return (
        <tr>
          <td>{t('MISSING_SHIPMENT_INFO')}</td>
        </tr>
      );
    }
    const podLinkTd = this.getPODLink(lineItemGroup.shipment_id);
    // Transitional origin/destination
    const originAddress = lineItemGroup.origin
      ? lineItemGroup.origin.address
      : lineItemGroup.originAddress;
    const originName =
      (lineItemGroup.origin
        ? lineItemGroup.origin.name
        : lineItemGroup.originName) || originAddress;
    const destinationAddress = lineItemGroup.destination
      ? lineItemGroup.destination.address
      : lineItemGroup.destinationAddress;
    const destinationName =
      (lineItemGroup.destination
        ? lineItemGroup.destination.name
        : lineItemGroup.destinationName) || destinationAddress;
    const momentDeliverBy = moment(lineItemGroup.jobDeliverBy);
    const deliverByDate = momentDeliverBy.isValid()
      ? momentDeliverBy.format('L')
      : lineItemGroup.jobDeliverBy;
    const momentClosed = moment(lineItemGroup.jobClosed);
    const closedDate = momentClosed.isValid()
      ? momentClosed.format('L')
      : lineItemGroup.jobClosed;

    const podVerified =
      typeof lineItemGroup.is_pod_valid !== 'undefined' &&
      lineItemGroup.is_pod_valid !== null;

    return (
      <tr key={lineItemGroup.shipment_id}>
        <td>{deliverByDate}</td>
        <td>
          <Link to={getShipmentDetailUrl(lineItemGroup.shipment_id)}>
            {lineItemGroup.shipmentId}
          </Link>
        </td>
        <td>
          {podVerified && (
            <CapabilityCircle checked={lineItemGroup.is_pod_valid} />
          )}
          {!podVerified && <span>{titleize(t('PENDING'))}</span>}
        </td>
        <td title={originAddress}>{originName}</td>
        <td title={destinationAddress}>{destinationName}</td>
        <td>{renderJobLoad(lineItemGroup.loads)}</td>
        <td>{lineItemGroup.weight}</td>
        <td>{lineItemGroup.driverName}</td>
        <td>{formatMoney(lineItemGroup.total, this.props.currency)}</td>
        {podLinkTd}
        <td>{closedDate}</td>
      </tr>
    );
  };

  render() {
    const lineItemGroups = this.props.lineItemGroups;
    const { t } = this.props;
    if (!lineItemGroups || lineItemGroups.length === 0) {
      return renderNoJobs();
    }

    const { jobList } = this.state;

    return (
      <div className="overflow-x-auto">
        <table className="default-table">
          <thead>
            <tr>
              <th>{t('DATE', { count: 1 })}</th>
              <th>{t('SHIPMENT', { count: 1 })}</th>
              <th>{t('POD_VERIFICATION')}</th>
              <th>{t('ORIGIN')}</th>
              <th>{t('DESTINATION')}</th>
              <th>{t('LOAD')}</th>
              <th>{t('WEIGHT')}</th>
              <th>{t('DRIVER', { count: 1 })}</th>
              <th>{t('TOTAL')}</th>
              <th>{t('POD')}</th>
              <th>{t('CLOSED_DATE')}</th>
            </tr>
          </thead>
          <tbody>{jobList}</tbody>
        </table>
      </div>
    );
  }
}

JobHistory.propTypes = {
  lineItemGroups: PropTypes.array,
  // context: PropTypes.string.isRequired,
};

JobHistory.defaultProps = {
  lineItemGroups: [],
};
export default withTranslation()(JobHistory);
